/**
 * Push Notifications Hooks
 * React Query hooks for push notification operations
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { pushApi } from '@/lib/api/push';
import type {
  SendTestNotificationRequest,
  PushTokensResponse,
  PushLogsResponse,
  PushStats,
  PushUsersResponse,
  PushContentAssistRequest,
  PushContentTemplateResponse,
} from '@/types/push';

/**
 * Send test notification mutation
 */
export function useSendTestNotification() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: SendTestNotificationRequest) => pushApi.sendTest(data),
    onSuccess: () => {
      // Invalidate logs and stats to refresh data
      queryClient.invalidateQueries({ queryKey: ['push', 'logs'] });
      queryClient.invalidateQueries({ queryKey: ['push', 'stats'] });
    },
  });
}

/**
 * Query push notification logs
 * @param params.status_filter - Filter by status ('sent' or 'failed')
 * @param params.page - Page number (1-based)
 * @param params.limit - Items per page (max: 50)
 * @param params.grouped - Group logs by batch (default: true)
 */
export function usePushLogs(params?: {
  status_filter?: string;
  page?: number;
  limit?: number;
  grouped?: boolean;
}): ReturnType<typeof useQuery<PushLogsResponse>> {
  return useQuery({
    queryKey: ['push', 'logs', params],
    queryFn: () => pushApi.getLogs(params),
    refetchInterval: 10000, // Refresh every 10 seconds
  });
}

/**
 * Query push notification statistics
 */
export function usePushStats(): ReturnType<typeof useQuery<PushStats>> {
  return useQuery({
    queryKey: ['push', 'stats'],
    queryFn: () => pushApi.getStats(),
    refetchInterval: 30000, // Refresh every 30 seconds
  });
}

/**
 * Query push tokens
 * @param params.platform - Filter by platform ('ios' or 'android')
 * @param params.active_only - Only return active tokens
 * @param params.page - Page number (1-based)
 * @param params.limit - Items per page (max: 100)
 */
export function usePushTokens(params?: {
  platform?: string;
  active_only?: boolean;
  page?: number;
  limit?: number;
}): ReturnType<typeof useQuery<PushTokensResponse>> {
  return useQuery({
    queryKey: ['push', 'tokens', params],
    queryFn: () => pushApi.getTokens(params),
    refetchInterval: 30000, // Refresh every 30 seconds
  });
}

/**
 * Query push notification users
 * @param params.search - Search by name or user_id
 * @param params.has_token - Only return users with active push tokens
 * @param params.page - Page number (1-based)
 * @param params.limit - Items per page (max: 100)
 */
export function usePushUsers(params?: {
  search?: string;
  has_token?: boolean;
  page?: number;
  limit?: number;
}): ReturnType<typeof useQuery<PushUsersResponse>> {
  return useQuery({
    queryKey: ['push', 'users', params],
    queryFn: () => pushApi.getUsers(params),
  });
}

/**
 * Generate push notification content using AI agent
 * @returns Mutation for generating push content
 */
export function usePushContentAssist() {
  return useMutation({
    mutationFn: (data: PushContentAssistRequest) => pushApi.contentAssist(data),
  });
}

/**
 * Get system prompt template for LLM mode
 * @returns Query for the system prompt template
 */
export function usePushContentTemplate(): ReturnType<typeof useQuery<PushContentTemplateResponse>> {
  return useQuery({
    queryKey: ['push', 'content-assist', 'template'],
    queryFn: () => pushApi.getContentAssistTemplate(),
    staleTime: Infinity, // Template rarely changes
  });
}
